import { uniqBy } from "ramda";
import { useMemo } from "react";
import { useLanguage } from "./useLanguage";

const dateTimeFormatOptionsValues: Partial<
  Record<keyof Intl.DateTimeFormatOptions, (string | boolean)[]>
> = {
  year: ["numeric"],
  month: ["numeric", "narrow", "short", "long"],
  day: ["numeric"],
  hour: ["numeric"],
  minute: ["numeric"],
  second: ["numeric"],
  hour12: [true, false],
};

const mustHave: Partial<
  Record<keyof Intl.DateTimeFormatOptions, (keyof Intl.DateTimeFormatOptions)[]>
> = {
  day: ["year", "month"],
  hour: ["minute"],
  minute: ["hour"],
  second: ["minute", "hour"],
};

const allPossibleCombinations =
  generateDateTimeFormatCombinations().filter(isValid);

export function useAllFormatOptions(): Intl.DateTimeFormatOptions[] {
  const language = useLanguage();

  return useMemo(() => {
    // Chosen so that each part is different
    const date = new Date("2023-12-31T23:59:42");

    return uniqBy((options) => {
      return new Intl.DateTimeFormat(language, options).format(date);
    }, allPossibleCombinations);
  }, [language]);
}

function generateDateTimeFormatCombinations(): Intl.DateTimeFormatOptions[] {
  const keys = Object.keys(
    dateTimeFormatOptionsValues,
  ) as (keyof Intl.DateTimeFormatOptions)[];

  const combinations: Intl.DateTimeFormatOptions[] = [];

  function generateCombination(
    index: number,
    currentCombination: Intl.DateTimeFormatOptions,
  ) {
    if (index === keys.length) {
      combinations.push({ ...currentCombination });
      return;
    }

    const key = keys[index];
    const values = [...dateTimeFormatOptionsValues[key]!, undefined];

    for (const value of values) {
      generateCombination(index + 1, { ...currentCombination, [key]: value });
    }
  }

  generateCombination(0, {});

  return combinations;
}

function isValid(option: Intl.DateTimeFormatOptions): boolean {
  const hasNoMissingFields = Object.entries(option).every(([key, value]) => {
    return (
      !value || isValueAllowed(option, key as keyof Intl.DateTimeFormatOptions)
    );
  });
  const hasTime = option.hour;
  const hasDate = option.year || option.month || option.day;
  const hasMonthAndDate = option.month && option.day;
  const timeAllowed = !hasDate || hasMonthAndDate;
  const ambiguousMonth =
    ["numeric", "narrow"].includes(String(option.month)) &&
    !option.year &&
    !option.day;

  // Don't allow combinations that are missing required fields or that have a time
  return Boolean(
    hasNoMissingFields && (!hasTime || timeAllowed) && !ambiguousMonth,
  );
}

function isValueAllowed(
  options: Intl.DateTimeFormatOptions,
  key: keyof Intl.DateTimeFormatOptions,
): boolean {
  const requiredKeys = mustHave[key] ?? [];

  return !requiredKeys.some((requiredKey) => !options[requiredKey]);
}
